import { View } from 'foundations-components/components/View';
import styled from 'foundations-theming/utils';
import { MerchCardColumns } from 'pricing-pages-ui-components/components/MerchCardColumns';
import { breakpointValues } from 'pricing-pages-ui-components/constants/Breakpoints';
import { jsx as _jsx } from "react/jsx-runtime";
const MOBILE_EM = (breakpointValues.tablet - 1) / 16;

/*
 * At narrow viewports, override the inner PPUIGrid's column template to fixed-width
 * columns and enable horizontal scroll — same carousel UX, no JS breakpoint check.
 * Content stays in the DOM at all viewport widths so crawlers always see desktop HTML.
 *
 * Scroll snap uses `& > * > *` to set scrollSnapAlign on each PPUIGrid item. Because
 * all items in the same column share the same x-position, the browser deduplicates
 * them into one snap point per card column (0, 75vw, 150vw, …). scrollPaddingInlineStart
 * aligns those snap points with the visual left gutter so snapped cards land flush.
 */
const MobileScrollContainer = styled.div.withConfig({
  displayName: "PublicHubMerchCardLayout__MobileScrollContainer"
})(({
  theme,
  $columns
}) => ({
  [`@media (max-width: ${MOBILE_EM}em)`]: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    scrollSnapType: 'x mandatory',
    paddingBlockEnd: theme.space[100],
    paddingInlineStart: theme.space[300],
    paddingInlineEnd: theme.space[300],
    scrollPaddingInlineStart: theme.space[300],
    /*
     * PageSection (the parent) applies paddingInline: space[300] at mobile.
     * Negate it so the scroll container extends edge-to-edge, then restore
     * inner padding via paddingInlineStart/End above.
     */
    marginInline: `-${theme.space[300]}`,
    '&::-webkit-scrollbar': {
      display: 'none'
    },
    '& > *': {
      /*
       * Expand the PPUIGrid beyond MobileScrollContainer's width so the
       * overflow-x: auto scroll activates. minInlineSize doesn't conflict
       * with PPUIGrid's own gridTemplateColumns — the grid then divides
       * this expanded width equally, giving each column min(75vw, 475px).
       * 75vw (vs 80vw) leaves a more intentional peek of the next card.
       */
      minInlineSize: `calc(${$columns} * min(75vw, 475px))`
    },
    '& > * > *': {
      scrollSnapAlign: 'start'
    }
  }
}));
export const PublicHubMerchCardLayout = ({
  children,
  columns,
  'data-test-id': dataTestId = 'public-merch-card-layout'
}) => {
  return /*#__PURE__*/_jsx(View, {
    position: "relative",
    "data-test-id": dataTestId,
    children: /*#__PURE__*/_jsx(MobileScrollContainer, {
      $columns: columns,
      children: /*#__PURE__*/_jsx(MerchCardColumns, {
        columns: columns,
        children: children
      })
    })
  });
};